// src/components/InterestForm.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "./ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

/* 🔐 LOCKED PREBOOK PRICES */
const PREBOOK_PRICES = {
  "Zen-S": 1499,
  "Zen-S Pro": 1999,
  "Zen-M": 1999,
  "Zen-M Pro": 1999,
  "Zen-L": 2999,
  "Zen-L Pro": 7999,
};

export const InterestForm = () => {
  const navigate = useNavigate();

  const [data, setData] = useState({
    length: "",
    width: "",
    usage: "",
    occupants: "",
    pets: "",
    city: "",
  });

  const [area, setArea] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);

  const isOffice = data.usage === "office";

  /* ---------------- RECOMMEND MODEL ---------------- */
  function recommendModel() {
    const length = Number(data.length);
    const width = Number(data.width);
    const calculatedArea = length * width;

    if (!calculatedArea || calculatedArea <= 0) return;

    setArea(calculatedArea);

    let base = "Zen-S";
    if (calculatedArea > 300) base = "Zen-M";
    if (calculatedArea > 500) base = "Zen-L";

    const list = [base, `${base} Pro`];
    setRecommendations(list);
    setSelectedModel(list[0]);
  }

  /* ---------------- PROCEED TO CHECKOUT ---------------- */
  function handleProceed() {
    if (!selectedModel) return;

    const amount = PREBOOK_PRICES[selectedModel];
    if (!amount) {
      alert("Pricing configuration error. Please contact support.");
      return;
    }

    navigate("/prebook/checkout", {
      state: {
        model: selectedModel,
        amount,
        city: data.city,
        roomArea: area,
        usage: data.usage,
        occupants: data.occupants,
        pets: data.pets,
        source: "interest-form", // 🔑 IMPORTANT FOR BACKEND
      },
    });
  }

  return (
    <section
      id="prebook"
      className="py-24 px-4 sm:px-6 lg:px-8 bg-[var(--aerzen-blue)]/5"
    >
      <div className="max-w-4xl mx-auto">

        {/* HEADER */}
        <div className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl font-bold mb-4">
            Help Me Choose My Ideal Air Purifier
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Clean air decisions made simple — designed for Indian homes.
          </p>
        </div>

        <Card className="rounded-2xl shadow-xl border">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl">
              Your Space · Your Usage · Your Safety
            </CardTitle>
            <CardDescription>
              Enter your room dimensions — we’ll calculate the rest
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">

            {/* ROOM SIZE */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Room Length (ft)</Label>
                <Input
                  type="number"
                  value={data.length}
                  onChange={(e) =>
                    setData({ ...data, length: e.target.value })
                  }
                />
              </div>
              <div>
                <Label>Room Width (ft)</Label>
                <Input
                  type="number"
                  value={data.width}
                  onChange={(e) =>
                    setData({ ...data, width: e.target.value })
                  }
                />
              </div>
            </div>

            {/* USAGE */}
            <div>
              <Label>Room Usage</Label>
              <Select onValueChange={(v) => setData({ ...data, usage: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select room type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bedroom">Bedroom</SelectItem>
                  <SelectItem value="living">Living Room</SelectItem>
                  <SelectItem value="office">Office</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {!isOffice && (
              <>
                <div>
                  <Label>Who lives here?</Label>
                  <Select onValueChange={(v) => setData({ ...data, occupants: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select option" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="adults">Adults</SelectItem>
                      <SelectItem value="children">Children</SelectItem>
                      <SelectItem value="elderly">Elderly</SelectItem>
                      <SelectItem value="asthma">Asthma Patient</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Pets at home?</Label>
                  <Select onValueChange={(v) => setData({ ...data, pets: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Yes / No" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div>
              <Label>City</Label>
              <Input
                value={data.city}
                onChange={(e) =>
                  setData({ ...data, city: e.target.value })
                }
              />
            </div>

            <Button
              onClick={recommendModel}
              className="w-full py-6 text-lg bg-[var(--aerzen-blue)]"
            >
              Find My Ideal Purifier →
            </Button>

            {recommendations.length > 0 && (
              <div className="mt-8 p-6 bg-blue-50 rounded-xl">
                <p className="text-sm mb-4">
                  Room size: <strong>{area} sq ft</strong>
                </p>

                <div className="grid sm:grid-cols-2 gap-4">
                  {recommendations.map((m) => (
                    <button
                      key={m}
                      onClick={() => setSelectedModel(m)}
                      className={`p-4 rounded-lg border text-left ${
                        selectedModel === m
                          ? "border-blue-600 bg-white shadow"
                          : "border-gray-300 bg-white"
                      }`}
                    >
                      <div className="font-bold">{m}</div>
                      <div className="text-sm text-gray-600">
                        Pre-Book ₹{PREBOOK_PRICES[m].toLocaleString("en-IN")}
                      </div>
                    </button>
                  ))}
                </div>

                <Button
                  onClick={handleProceed}
                  disabled={!selectedModel}
                  className="w-full mt-6 bg-black py-5 rounded-xl disabled:opacity-60"
                >
                  Proceed to Secure Payment →
                </Button>
              </div>
            )}

          </CardContent>
        </Card>
      </div>
    </section>
  );
};
